import type { CartCheckout, ShoppingCart, SignedAction } from "@kuroo/contracts";
import type { WaitlistEntry } from "./client-catalog-types";

export type SdkCartContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function waitlist(context: SdkCartContext): Promise<WaitlistEntry[]> {
  return context.request<WaitlistEntry[]>("/api/v1/waitlist");
}

export function offerWaitlistEntry(context: SdkCartContext, entryId: string, ttlSeconds?: number): Promise<SignedAction & { status: string }> {
  return context.request<SignedAction & { status: string }>(`/api/v1/waitlist/${encodeURIComponent(entryId)}/offer`, { method: "POST", body: JSON.stringify({ ttlSeconds }) });
}

export function acceptWaitlistEntry(context: SdkCartContext, entryId: string): Promise<{ id: string; status: "accepted" }> {
  return context.request<{ id: string; status: "accepted" }>(`/api/v1/waitlist/${encodeURIComponent(entryId)}/accept`, { method: "POST", body: "{}" });
}

export function cancelWaitlistEntry(context: SdkCartContext, entryId: string): Promise<{ id: string; status: "cancelled" }> {
  return context.request<{ id: string; status: "cancelled" }>(`/api/v1/waitlist/${encodeURIComponent(entryId)}/cancel`, { method: "POST", body: "{}" });
}

export function cart(context: SdkCartContext): Promise<ShoppingCart> {
  return context.request<ShoppingCart>("/api/v1/cart");
}

export function addCartItem(context: SdkCartContext, input: { serviceId?: string; packageId?: string; eventPeriodId?: string; ticketId?: string; quantity?: number; metadata?: Record<string, unknown> }): Promise<ShoppingCart> {
  return context.request<ShoppingCart>("/api/v1/cart/items", { method: "POST", body: JSON.stringify(input) });
}

export function updateCartItem(context: SdkCartContext, itemId: string, quantity: number): Promise<ShoppingCart> {
  return context.request<ShoppingCart>(`/api/v1/cart/items/${encodeURIComponent(itemId)}`, { method: "PATCH", body: JSON.stringify({ quantity }) });
}

export function removeCartItem(context: SdkCartContext, itemId: string): Promise<ShoppingCart> {
  return context.request<ShoppingCart>(`/api/v1/cart/items/${encodeURIComponent(itemId)}`, { method: "DELETE" });
}

export function checkoutCart(context: SdkCartContext, input: { confirm?: boolean; paymentMethod?: "mercadopago" | "stripe" | "paypal" | "on_site"; returnUrls?: { success?: string; pending?: string; failure?: string }; idempotencyKey?: string } = {}): Promise<CartCheckout> {
  const idempotencyKey = input.idempotencyKey ?? `cart-checkout-${Date.now()}`;
  return context.request<CartCheckout>("/api/v1/cart/checkout", { method: "POST", body: JSON.stringify({ ...input, idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
}

export function joinWaitlist(context: SdkCartContext, input: { serviceId?: string; eventPeriodId?: string; email?: string; persons?: number; desiredStartAt?: string; desiredEndAt?: string }): Promise<{ id: string; status: string }> {
  return context.request<{ id: string; status: string }>("/api/v1/waitlist", { method: "POST", body: JSON.stringify(input) });
}
